import { ResumeCustomSection } from '../../types';
import { Sparkles, Star, Users } from 'lucide-react';
import { RichTextEditor } from '../ui/RichTextEditor';

const SECTION_TEMPLATES = [
    {
        title: 'Custom Section',
        description: 'Add any topic that does not fit elsewhere.',
        Icon: Sparkles,
    },
    {
        title: 'Extra-curricular Activities',
        description: 'Add clubs, volunteering, or competitions.',
        Icon: Star,
    },
    {
        title: 'References',
        description: 'Describe professional or academic references.',
        Icon: Users,
    },
];

interface AdditionalSectionsSectionProps {
    customSections?: ResumeCustomSection[];
    onChange: (sections: ResumeCustomSection[]) => void;
}

const createNewSection = (title: string): ResumeCustomSection => ({
    id: `${title}-${Date.now()}`,
    title,
    url: '',
    content: '',
    location: '',
    startDate: '',
    endDate: '',
});

export function AdditionalSectionsSection({
    customSections = [],
    onChange,
}: AdditionalSectionsSectionProps) {
    const handleAddSection = (title: string) => {
        onChange([...customSections, createNewSection(title)]);
    };

    const handleUpdate = (id: string, values: Partial<ResumeCustomSection>) => {
        onChange(
            customSections.map(section => (section.id === id ? { ...section, ...values } : section))
        );
    };

    const handleRemove = (id: string) => {
        onChange(customSections.filter(section => section.id !== id));
    };

    return (
        <div className="space-y-4">
            <div className="bg-gray-50 border border-dashed border-gray-300 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                    <div>
                        <p className="text-sm font-semibold text-gray-700">Add Additional Sections</p>
                        <p className="text-xs text-gray-500">
                            Pick a section to insert an extra block in your resume.
                        </p>
                    </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-2">
                    {SECTION_TEMPLATES.map(template => (
                        <button
                            key={template.title}
                            type="button"
                            onClick={() => handleAddSection(template.title)}
                            className="border border-gray-200 rounded-lg p-3 text-left flex items-start gap-3 hover:border-primary-500 transition"
                        >
                            <template.Icon className="h-5 w-5 text-primary-600" />
                            <div>
                                <p className="text-sm font-semibold text-gray-800">
                                    {template.title}
                                </p>
                                <p className="text-xs text-gray-500">{template.description}</p>
                            </div>
                        </button>
                    ))}
                </div>
            </div>

            {customSections.length > 0 && (
                <div className="space-y-3">
                    {customSections.map(section => (
                        <div key={section.id} className="border border-gray-200 rounded-lg p-3 bg-white shadow-sm space-y-3">
                            <div className="flex items-center justify-between">
                                <input
                                    type="text"
                                    value={section.title}
                                    onChange={(e) => handleUpdate(section.id, { title: e.target.value })}
                                    className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                                    placeholder="Activity name, book title, etc."
                                />
                                <button
                                    type="button"
                                    onClick={() => handleRemove(section.id)}
                                    className="text-red-500 text-xs font-semibold ml-3"
                                >
                                    Remove
                                </button>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                <input
                                    type="text"
                                    value={section.location || ''}
                                    onChange={(e) => handleUpdate(section.id, { location: e.target.value })}
                                    className="input"
                                    placeholder="City / Institution"
                                />
                                <input
                                    type="url"
                                    value={section.url || ''}
                                    onChange={(e) => handleUpdate(section.id, { url: e.target.value })}
                                    className="input"
                                    placeholder="Link (optional)"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                <input
                                    type="text" inputMode="numeric" pattern="[0-9]{4}-[0-9]{2}"
                                    placeholder="YYYY-MM"
                                    value={section.startDate || ''}
                                    onChange={(e) => handleUpdate(section.id, { startDate: e.target.value })}
                                    className="input"
                                />
                                <input
                                    type="text" inputMode="numeric" pattern="[0-9]{4}-[0-9]{2}"
                                    placeholder="YYYY-MM"
                                    value={section.endDate || ''}
                                    onChange={(e) => handleUpdate(section.id, { endDate: e.target.value })}
                                    className="input"
                                />
                            </div>
                            <RichTextEditor
                                value={section.content}
                                onChange={(value) => handleUpdate(section.id, { content: value })}
                                placeholder="Describe this section..."
                                minHeight="100px"
                            />
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}



